/**
 * DisplayOptionsMenu — gear icon dropdown to toggle display preferences
 *
 * Top group: localStorage-backed display prefs (Reasoning, Tool details,
 * Model info, Timestamps, Wrap code).
 *
 * Bottom group: server-backed behavior prefs (allowSubagentModelUpgrade).
 * These are persisted via /api/users/me/settings because the WORKER (not
 * the browser) reads them when running the agent — localStorage wouldn't
 * be visible from the worker. See issue #47.
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Settings2, Loader2 } from 'lucide-react'
import { request } from '@papan/api-client'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import type { DisplayOptions } from './useDisplayOptions.js'

interface Props {
  options: DisplayOptions
  onToggle: (key: keyof DisplayOptions) => void
}

const DISPLAY_LABELS: { key: keyof DisplayOptions; label: string }[] = [
  { key: 'showReasoning', label: 'Reasoning' },
  { key: 'showToolDetails', label: 'Tool details' },
  { key: 'showModelInfo', label: 'Model info' },
  { key: 'showTimestamps', label: 'Timestamps' },
  { key: 'wrapDiffLines', label: 'Wrap code' },
]

interface BehaviorSettings {
  allowSubagentModelUpgrade?: boolean
}

export function DisplayOptionsMenu({ options, onToggle }: Props) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  // The dropdown portals to <body>, so it needs the theme class re-applied
  // (it's rendered outside any ThemedArea). Matches ChatPanel's dialogs.
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  // Fixed-position anchor computed from the trigger rect on open — the menu
  // used to be absolutely positioned inside .ws-tabrow (overflow:hidden),
  // which clipped it to nothing on the mobile session-tabs row.
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null)
  const openMenu = () => {
    const r = btnRef.current?.getBoundingClientRect()
    if (r) setPos({ top: r.bottom + 4, right: Math.max(8, window.innerWidth - r.right) })
    setOpen(true)
  }

  // Server-backed behavior prefs. Loaded once on first menu open.
  const [behavior, setBehavior] = useState<BehaviorSettings | null>(null)
  const [behaviorLoading, setBehaviorLoading] = useState(false)
  const [behaviorSaving, setBehaviorSaving] = useState(false)
  // Tracked via ref so we don't depend on `behaviorLoading` in the effect
  // — that would re-fire on the set-loading-true render, cancel its own
  // promise via cleanup, and leave the spinner stuck forever.
  const fetchStartedRef = useRef(false)

  // Close on click-outside — the panel now lives in a body portal, so we
  // check both the trigger and the portalled panel.
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Lazy-fetch behavior prefs the first time the menu opens. No need to
  // pay the request cost for users who never open this menu.
  useEffect(() => {
    if (!open || fetchStartedRef.current) return
    fetchStartedRef.current = true
    setBehaviorLoading(true)
    let cancelled = false
    request<{ settings: BehaviorSettings }>('/api/users/me/settings')
      .then(res => {
        if (!cancelled) setBehavior(res.settings ?? {})
      })
      .catch(() => {
        if (!cancelled) setBehavior({})
      })
      .finally(() => {
        if (!cancelled) setBehaviorLoading(false)
      })
    return () => { cancelled = true }
  }, [open])

  const toggleAllowUpgrade = useCallback(async () => {
    const prev = behavior?.allowSubagentModelUpgrade === true
    const next = !prev
    setBehavior(b => ({ ...(b ?? {}), allowSubagentModelUpgrade: next })) // optimistic
    setBehaviorSaving(true)
    try {
      await request('/api/users/me/settings', {
        method: 'PATCH',
        body: JSON.stringify({ allowSubagentModelUpgrade: next }),
        headers: { 'Content-Type': 'application/json' },
      })
    } catch {
      setBehavior(b => ({ ...(b ?? {}), allowSubagentModelUpgrade: prev })) // revert
    } finally {
      setBehaviorSaving(false)
    }
  }, [behavior])

  const allowUpgrade = behavior?.allowSubagentModelUpgrade === true

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => (open ? setOpen(false) : openMenu())}
        className="p-1 rounded themed-text-muted hover:themed-text hover:themed-bg-tertiary transition-colors"
        title="Display options"
      >
        <Settings2 size={13} />
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          className={`${themeClass} fixed w-56 rounded-md border themed-border themed-bg-secondary shadow-lg z-[100]`}
          style={{ top: pos.top, right: pos.right }}
        >
          <div className="py-1">
            {DISPLAY_LABELS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => onToggle(key)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary transition-colors"
              >
                <span>{label}</span>
                <div className={`w-7 h-4 rounded-full transition-colors flex items-center ${
                  options[key] ? 'bg-brand-600 justify-end' : 'bg-white/10 justify-start'
                }`}>
                  <div className="w-3 h-3 rounded-full bg-white mx-0.5 shadow-sm" />
                </div>
              </button>
            ))}

            <div className="my-1 border-t themed-border" />
            <div className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-wide themed-text-muted">Behavior</div>

            <button
              onClick={toggleAllowUpgrade}
              disabled={behaviorLoading || behaviorSaving}
              title="When enabled, the agent may run subtask LLM calls (task / run_batch_llm) on a higher model than your session default. Off = your model is the cost ceiling."
              className="w-full flex items-center justify-between px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary transition-colors disabled:opacity-50"
            >
              <span className="text-left flex-1 pr-2">Allow stronger subagent model</span>
              {behaviorLoading ? (
                <Loader2 size={12} className="animate-spin themed-text-muted" />
              ) : (
                <div className={`w-7 h-4 rounded-full transition-colors flex items-center shrink-0 ${
                  allowUpgrade ? 'bg-brand-600 justify-end' : 'bg-white/10 justify-start'
                }`}>
                  <div className="w-3 h-3 rounded-full bg-white mx-0.5 shadow-sm" />
                </div>
              )}
            </button>

            {/* Dev-only: reveal the tool-render preview + typography menus in
                the composer footer. Tree-shaken out of production builds. */}
            {import.meta.env.DEV && (
              <>
                <div className="my-1 border-t themed-border" />
                <div className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-wide themed-text-muted">Developer</div>
                <button
                  onClick={() => onToggle('showDevTools')}
                  title="Show the dev-only render preview and typography (font) menus in the composer."
                  className="w-full flex items-center justify-between px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary transition-colors"
                >
                  <span className="text-left flex-1 pr-2">Render &amp; font tools</span>
                  <div className={`w-7 h-4 rounded-full transition-colors flex items-center shrink-0 ${
                    options.showDevTools ? 'bg-brand-600 justify-end' : 'bg-white/10 justify-start'
                  }`}>
                    <div className="w-3 h-3 rounded-full bg-white mx-0.5 shadow-sm" />
                  </div>
                </button>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
